import { useState } from 'react';
import { Link } from 'react-router-dom';

/* ═══════════════════════════════════════════════════════════════════════════
 * L'EN-TÊTE DE LA VITRINE, ÉCRIT UNE SEULE FOIS
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ AUCUN CHANGEMENT VISUEL. Le markup et les classes sont REPRIS À
 *    L'IDENTIQUE de l'en-tête de l'accueil : `vt-nav`, `vt-nav-brand`,
 *    `vt-nav-pastille`, `vt-nav-logo`, `vt-nav-wordmark`, `vt-nav-links`,
 *    `vt-nav-link`, `vt-nav-cta`, `vt-nav-burger`, `vt-mobile-menu`,
 *    `vt-mobile-link`, `vt-mobile-cta`. **Aucune classe nouvelle, et
 *    `LandingPage.css` n'est pas touché par ce lot.**
 *
 * ⚠️ POURQUOI IL EXISTE — LE DÉFAUT ÉTAIT STRUCTUREL. L'en-tête était RECOPIÉ
 *    dans trois fichiers (`LandingPage`, `FaqPage`, `QuiSommesNous`), et deux
 *    pages publiques avaient divergé :
 *      · `/mentions-legales` et `/confidentialite` : **0 logo, 0 signature
 *        GSS-360** — un `<span>GSS-360</span>` nu, et une barre réduite à
 *        « Retour à l'accueil / Se connecter ».
 *      · `/legal` : **aucun en-tête public du tout**.
 *    Trois endroits à tenir à jour, un seul qu'on pense à ouvrir. Les cinq
 *    pages sont désormais conformes PAR CONSTRUCTION.
 *
 * ⚠️ LES LIENS DE SECTION SONT ABSOLUS : `/#fonctionnalites`, `/#tarifs`,
 *    `/#demo` — et non `#fonctionnalites`, qui ne vaut que sur l'accueil. Un
 *    en-tête partagé est servi depuis cinq adresses ; une ancre relative y
 *    désignerait une section absente de la page courante.
 *    ⓘ CONSÉQUENCE SUR L'ACCUEIL, ASSUMÉE ET VALIDÉE PAR DOM : le mécanisme
 *      change (navigation react-router au lieu d'une ancre native du
 *      navigateur), le résultat ne change pas — `LandingPage` lit le hash et
 *      défile. C'est `LandingPage` qui porte cette logique, pas ce composant.
 *
 * ⓘ `<Link>` ET PAS `<a href>`, pour tous les liens internes. Un `<a href>`
 *   recharge l'application entière, et surtout : au rechargement, le
 *   navigateur traite le `#demo` AVANT que React ne monte — la section n'existe
 *   pas encore, donc aucun défilement. C'était la cause du « j'atterris en haut
 *   de la home ». `FaqPage` et `QuiSommesNous` utilisaient déjà `<Link>` : ce
 *   composant généralise le comportement déjà servi en production.
 * ═══════════════════════════════════════════════════════════════════════════ */

/* ⓘ Une seule liste, donc le même ordre sur les cinq pages. */
const LIENS = [
  { to: '/qui-sommes-nous', libelle: 'Qui sommes-nous' },
  { to: '/#fonctionnalites', libelle: 'Fonctionnalités' },
  { to: '/#tarifs', libelle: 'Tarifs' },
  { to: '/faq', libelle: 'FAQ' },
  { to: '/#demo', libelle: 'Démo' },
];

export default function VitrineNav() {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      <nav className="vt-nav">
        {/* ⚠️ UNE SEULE MARQUE, DEUX NATURES. Le logo Gladiator vaut pour
            l'éditeur ; la signature « GSS-360 » nomme le PRODUIT. Le logo est
            une image parce qu'il en est une ; le nom du produit reste du TEXTE —
            il se lit, se sélectionne, se traduit et s'indexe. Le dépôt ne
            contient aucun logo GSS-360, et même s'il en contenait un ce choix
            tiendrait.
            ⚠️ LE LIEN PORTE L'ENSEMBLE : un seul lien vers « / », donc une seule
               cible de clic. L'`alt` du logo nomme ce que l'image MONTRE ;
               l'`aria-label` du lien nomme sa DESTINATION.
            ⚠️ PASTILLE CLAIRE, ET C'EST LE FICHIER QUI L'IMPOSE : le PNG est
               OPAQUE quasi blanc (`RGBA(253,253,253,255)` aux quatre coins,
               mesuré). Posé nu sur le fond sombre de la barre, il devient un
               rectangle lumineux. L'envelopper rend la chose VOULUE.
            ⚠️ `width`/`height` intrinsèques déclarés (292×234) : sans eux le
               navigateur réserve 0 et la barre saute au chargement.
            ⚠️ L'ORDRE DE CESSION EST STRUCTUREL : la pastille est
               `flex-shrink: 0` (l'image ne cède JAMAIS), la signature porte
               `min-width: 0` (elle cède la PREMIÈRE). Rien n'est codé en
               `display:none` à une largeur arbitraire. */}
        <Link to="/" className="vt-nav-brand" aria-label="GSS-360 — accueil">
          <span className="vt-nav-pastille">
            <img
              src="/logo-gladiator.png"
              alt="Gladiator Security Systems"
              className="vt-nav-logo"
              width="292"
              height="234"
            />
          </span>
          <span className="vt-nav-wordmark">GSS<span className="vt-nav-wordmark-accent">-360</span></span>
        </Link>
        <div className="vt-nav-links">
          {LIENS.map((l) => (
            <Link key={l.to} to={l.to} className="vt-nav-link">{l.libelle}</Link>
          ))}
          <Link to="/login" className="vt-nav-cta">Se connecter</Link>
        </div>
        <button
          type="button"
          className="vt-nav-burger"
          aria-label={menuOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {menuOpen ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>
      </nav>

      {/* Menu mobile — ⓘ `.vt-mobile-menu` est `display: none` hors du point de
          rupture 768 px ; c'est le CSS qui décide, pas ce composant. */}
      {menuOpen && (
        <div className="vt-mobile-menu">
          {LIENS.map((l) => (
            <Link key={l.to} to={l.to} className="vt-mobile-link" onClick={() => setMenuOpen(false)}>
              {l.libelle}
            </Link>
          ))}
          <Link to="/login" className="vt-mobile-cta" onClick={() => setMenuOpen(false)}>Se connecter</Link>
        </div>
      )}
    </>
  );
}
